import {
  ClickAwayListener,
  makeStyles,
  ListItem,
  ListItemText,
  Typography,
  useInfiniteScroll,
  useBreakpoint
} from '@paintscout/ui'
import type { MuiTheme as Theme, ListItemProps, StyleClasses } from '@paintscout/ui'
import type { Contact, User } from '@paintscout/api'
import React, { useRef } from 'react'
import classnames from 'classnames'
import moment from 'moment'
import { getOwnerDisplayName } from '@paintscout/util/builder'
import { useUsers } from '@paintscout/ui-business'

const getContactSourceDisplayName = (source: string): string => {
  switch (source) {
    case 'pipeline_deals':
      return 'Pipeline CRM'
    case 'service_titan':
      return 'Service Titan'
    case 'salesforce':
      return 'Salesforce'
    default:
      return ''
  }
}

const useStyles = makeStyles<Theme, ContactResultsListProps>(
  () => ({
    root: {
      height: '100%',
      maxHeight: '60vh',
      overflow: 'auto'
    },
    row: {}
  }),
  { name: 'ContactResultsList' }
)

export interface ContactResultsListProps {
  classes?: StyleClasses<typeof useStyles>
  disabled?: boolean
  loading?: boolean
  hasMore?: boolean
  results: Array<Partial<Contact> & { dealId?: number }>
  onLoadMore: () => any
  onContactClick: (contact: Partial<Contact & { dealId?: number }>) => any
  onClickAway?: (event: React.MouseEvent<Document, MouseEvent>) => void
}

function ContactResultsList(props: ContactResultsListProps) {
  const classes = useStyles(props)
  const { disabled, loading, hasMore, results, onContactClick, onLoadMore, onClickAway } = props
  const ref = useRef(null)
  // Read once for the whole list rather than per row — each item resolves its owner name against it.
  const { usersById } = useUsers()

  useInfiniteScroll({
    ref,
    onLoadMore,
    scrollPercentage: 95,
    disabled: disabled || loading || hasMore === false
  })

  return (
    <ClickAwayListener
      onClickAway={(event) => {
        if (onClickAway) onClickAway(event)
      }}
    >
      <div className={classes.root} ref={ref}>
        {results.map((contact, index) => (
          <ContactListItem
            className={`contact-list-${index}`}
            key={index}
            disabled={disabled}
            contact={contact}
            usersById={usersById}
            onClick={() => onContactClick(contact)}
          />
        ))}
      </div>
    </ClickAwayListener>
  )
}

/*******************************
 *         ContactListItem     *
 *******************************/
// Flex basis of the name/address column. It is the wrap trigger: when the row cannot fit
// this much plus the email/owner block, the block wraps onto its own line and the row
// stacks. Driving the stack from the row's real width (not a viewport media query) keeps
// it correct in the narrow contact column of DealForm on a wide screen, and it cannot be
// defeated by zoom or a desktop-mode viewport. This layout regressed three times while it
// was tuned through viewport queries and fixed pixel widths (#18708, #19090, #21028).
const NAME_COLUMN_BASIS_PX = 220

// `name` makes the generated class deterministic (`ContactListItem-listItem-*`) instead of a
// hash, so the row's layout class can be asserted on. Matches `useStyles` above.
const useListItemStyles = makeStyles(
  (theme: Theme) => ({
    listItem: {
      maxWidth: '100%',
      flexWrap: 'wrap',
      // `button` renders the row as a ButtonBase, and `.MuiButtonBase-root` sets all three of
      // these (`inline-flex` / `center` / `center`) at the same specificity as a plain
      // makeStyles class. MUI v4 injects its own sheets first, so a single class does win —
      // but only by sheet order. `&&` doubles the class so the row layout does not depend on
      // that. This component already shipped one dead layout rule (#22473).
      '&&': {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between'
      }
    },
    listItemText: {
      // gutter between the two columns; `column-gap` would need Safari 14.1+
      paddingRight: theme.spacing(1),
      // without this a long unbroken word keeps the column at its min-content width and
      // spills over the email block instead of shrinking
      minWidth: 0,
      overflowWrap: 'break-word',
      // `.MuiListItemText-root` sets `flex: 1 1 auto`; this basis is the wrap trigger, so
      // take it at higher specificity rather than relying on sheet order
      '&&': {
        flex: `1 1 ${NAME_COLUMN_BASIS_PX}px`
      }
    },
    details: {
      // shrink to fit: `space-between` pins it to the right edge while it shares the row,
      // and it starts at the left of its own line once the row wraps
      flex: '0 1 auto',
      minWidth: 0,
      textAlign: 'left'
    },
    secondaryText: {
      color: theme.paintscout.palette.brand.Neutral.P70,
      fontStyle: 'italic',
      overflowWrap: 'break-word'
    },
    sourceName: {
      color: theme.paintscout.palette.brand.Neutral.P70,
      fontStyle: 'italic',
      maxWidth: '100%'
    },
    email: {
      fontStyle: 'italic',
      overflowWrap: 'break-word',
      wordBreak: 'break-all',
      maxWidth: '100%'
    },
    owner: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '100%',
      fontWeight: 600
    }
  }),
  { name: 'ContactListItem' }
)

function ContactListItem({
  contact,
  usersById,
  ...props
}: {
  contact: Partial<Contact>
  usersById?: Record<string, User>
} & ListItemProps) {
  const classes = useListItemStyles({})
  const isSmallScreen = useBreakpoint((breakpoints) => breakpoints.down('sm'))
  // Live name, falling back to the contact's owner snapshot — see getOwnerDisplayName (#21800).
  const ownerName = getOwnerDisplayName({ owner: contact.owner, users: usersById })

  const primaryText = (
    <>
      <strong>
        {contact.first_name} {contact.last_name}
      </strong>
      {!!contact.company && ` - ${contact.company}`}
      {!!contact.stAppointmentDate && ` - ${moment(contact.stAppointmentDate)}`.split(' GMT')[0]}
    </>
  )

  const secondaryText = [contact.address, contact.city, contact.province].filter((item) => item).join(', ')
  const textBodySize = isSmallScreen ? 'body2' : 'body1'
  let contactSource = getContactSourceDisplayName(contact.source)
  let contactSourceSecondary = ''
  if (contact?.pldDeals?.length) {
    if (contact.pldDeals.length > 1) {
      contactSource = `Deal Count: ${contact.pldDeals.length}`
    } else {
      contactSource = `${contact.pldDeals[0].id} `
      contactSourceSecondary = `${contact.pldDeals[0].name}`
    }
  }

  return (
    // merge, don't overwrite: the parent passes its own `className`, and a bare
    // `className` before the spread is silently dropped (#22473)
    <ListItem {...(props as any)} divider button className={classnames(classes.listItem, props.className)}>
      <ListItemText
        classes={{ root: classes.listItemText, secondary: classes.secondaryText }}
        primary={primaryText}
        secondary={secondaryText}
        primaryTypographyProps={{
          variant: textBodySize
        }}
        secondaryTypographyProps={{
          variant: textBodySize
        }}
      />

      {/* no `align="right"`: the flex container places this block, and right-aligned text
          would read wrong once the row wraps and the block takes the full width */}
      <div className={classes.details}>
        <Typography variant={textBodySize} classes={{ root: classes.sourceName }}>
          {contactSource}
        </Typography>
        {contactSourceSecondary && (
          <Typography variant={textBodySize} classes={{ root: classes.sourceName }}>
            {contactSourceSecondary}
          </Typography>
        )}
        <Typography variant={textBodySize} classes={{ root: classes.email }}>
          {contact.email}
        </Typography>
        {!!ownerName && (
          <Typography variant={textBodySize} classes={{ root: classes.owner }}>
            {ownerName}
          </Typography>
        )}
      </div>
    </ListItem>
  )
}

export default ContactResultsList
